import { c as _c } from "react/compiler-runtime";
import { ActionIcon, Chip, Tooltip } from '@mantine/core';
import { isEmpty, pick, pipe } from 'remeda';
import { useState, useContext } from 'react';
import { QuestionCfg, zQuestionForecastPub } from 'shared/forecast/question';
import { notNull } from 'shared/type';
import { LabelForm, Markdown, ThemeOverride } from 'shared/ui';
import { QuestionResultsProps } from 'shared/forecast/results/QuestionResults';
import { BinForecast, ForecastBarNote } from 'shared/chart';
import { RichText } from 'components/form/RichText';
import { IconContext } from 'react-icons';
import { Forecast as F } from 'shared/forecast';
import { useForecasts } from './useForecasts';
import { fromEntries, isNullOrEmpty, notNullOrEmpty } from 'shared/collection';
import { useQuestionForm, useQuestionQuery } from './useQuestions';
import { O } from 'ts-toolbelt';
import { DbFormSubset, useDbForm } from 'components/hooks/useDbForm';
import { doSafe, sansOk } from 'shared/promise';
import { EditActionIcon } from './QuestionForm';
import { MdUndo } from 'react-icons/md';
import { compactString } from 'shared/text';
import { QuestionContext } from './questionComponentTypes';
export const useForecastNotes = t0 => {
  const $ = _c(18);
  const {
    question_id,
    qForm: qFormRaw
  } = t0;
  const t1 = !qFormRaw;
  let t2;
  if ($[0] !== question_id || $[1] !== t1) {
    t2 = {
      question_id,
      pick: _temp,
      disabled: t1
    };
    $[0] = question_id;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const qFormUnctronrolled = useQuestionForm(t2).form;
  const qForm = qFormRaw ?? qFormUnctronrolled;
  const publish = qFormRaw ? qFormRaw.values?.publish : qFormUnctronrolled.values?.publish;
  const {
    readonly
  } = useContext(QuestionContext);
  let t3;
  if ($[3] !== question_id) {
    t3 = {
      question_id
    };
    $[3] = question_id;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== readonly || $[6] !== t3) {
    t4 = {
      filter: t3,
      disabled: readonly
    };
    $[5] = readonly;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const uForecasts = useForecasts(t4);
  let t5;
  if ($[8] !== publish || $[9] !== qForm || $[10] !== question_id || $[11] !== readonly || $[12] !== uForecasts) {
    t5 = () => {
      if (readonly || !publish || !uForecasts || !question_id) {
        console.error("useForecastNotes: missing data, or readonly", {
          readonly,
          publish,
          uForecasts,
          question_id
        });
        return;
      }
      const updates = pipe(uForecasts.forecastGroups.map(_temp3).flat().map(f_0 => {
        const key = F.naturalKey({
          question_id,
          subquestion_id: f_0.subquestion_id,
          type: F.toQuestionType(f_0.cfg.type),
          code: f_0.cfg.code,
          created_by: f_0.created_by
        });
        const currentPub = publish?.forecasts[key] ?? {};
        if (currentPub.show) {
          return;
        }
        return [`publish.forecasts.${key}.show`, true] as [string, boolean];
      }), _temp4);
      qForm?.setValues(updates);
    };
    $[8] = publish;
    $[9] = qForm;
    $[10] = question_id;
    $[11] = readonly;
    $[12] = uForecasts;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const showAllNotes = t5;
  const t6 = !readonly;
  let t7;
  if ($[14] !== question_id || $[15] !== showAllNotes || $[16] !== t6) {
    t7 = {
      showAllNotes,
      question_id,
      isQuestionEditor: t6
    };
    $[14] = question_id;
    $[15] = showAllNotes;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
};
export type BinForecastApp = BinForecast & {
  id: string;
  key: string;
};
export type UseForecastNotes = ReturnType<typeof useForecastNotes>;
type ForecastNoteCustomProps = {
  forecast: O.Required<BinForecast & {
    key: string;
  }, 'notes'>;
  isQuestionEditor: boolean;
  question_id: string;
};
const ForecastNoteCustom = ({
  forecast: f,
  isQuestionEditor,
  question_id
}: ForecastNoteCustomProps) => {
  const {
    key
  } = f;
  const qCrud = useQuestionQuery({
    question_id,
    pick: r => r.cfg.publish.forecasts[key]
  });
  const publishState = doSafe(qCrud.data, d => d.ok ? sansOk(d) : undefined);
  const form = useDbForm({
    schema: zQuestionForecastPub,
    stored: publishState,
    debounceSave: 2000,
    onUpdate: async ({
      delta,
      cfg
    }) => {
      qCrud.updateCfg({
        id: question_id,
        cfg_updates: {
          [`publish.forecasts.${key}`]: cfg
        }
      });
    }
  });
  const [editing, setEditing] = useState(false);
  if (!isQuestionEditor || !qCrud.isSuccess) return <Markdown className='mr-1 mt-1 break-words'>{f.notes}</Markdown>;
  const pub = form.values ?? zQuestionForecastPub.parse({});
  const setPub = form.setValues;
  const hasModifiedNote = compactString(pub.note) != null;
  return qCrud.isSuccess ? <ThemeOverride override={{
    colorScheme: 'dark'
  }}>
        <IconContext.Provider value={{
      size: '16px',
      color: 'var(--mantine-color-gray-2)'
    }}>
          <ForecastBarNote forecast={f} menu={<div className='flex items-center gap-2'>
                <Tooltip zIndex={1000} classNames={{
          tooltip: 'border border-gray-500/50'
        }} label={<div>
                      <p>
                        {pub.show ? 'Visible in published version - click to hide' : 'Hidden in published version - click to show'}
                      </p>
                      <p className='mt-2 text-gray-500'>NOTE: Re-publish to see this change reflected</p>
                    </div>}>
                  <div>
                    <Chip variant='filled' color='teal' className='font-semibold' styles={{
              label: {
                opacity: pub.show ? 1 : 0.5
              }
            }} size='xs' checked={pub.show} onChange={show => {
              setPub({
                show
              });
            }}>
                      Show in published
                    </Chip>
                  </div>
                </Tooltip>
                <EditActionIcon size='md' editing={editing} setEditing={editing_0 => {
          console.log('editing', editing_0, pub.note);
          if (editing_0 && isNullOrEmpty(pub.note)) setPub({
            note: f.notes
          });
          return setEditing(editing_0);
        }} editLabel='Edit published note' />
                <div className='text-sm opacity-70'>{hasModifiedNote ? '(edited for publication)' : ''}</div>
              </div>} content={editing ? <div className='space-y-2 my-2'>
                  <div>
                    <div className='flex items-center gap-2'>
                      <LabelForm>Published note</LabelForm>
                      <Tooltip label='Revert to forecaster note' zIndex={1000}>
                        <div>
                          <ActionIcon onClick={() => {
                  setPub({
                    note: undefined
                  });
                  setEditing(false);
                }}>
                            <MdUndo />
                          </ActionIcon>
                        </div>
                      </Tooltip>
                    </div>
                    <RichText value={pub.note ?? ''} onChange={md => setPub({
            note: md
          })}></RichText>
                  </div>
                  <div>
                    <LabelForm>Forecaster note</LabelForm>
                    <Markdown className='mr-1 mt-1 break-words'>{f.notes}</Markdown>
                  </div>
                </div> : hasModifiedNote ? pub.note : f.notes} />
        </IconContext.Provider>
      </ThemeOverride> : undefined;
};
export const getNoteCustom = (props: UseForecastNotes): QuestionResultsProps<BinForecastApp>['noteCustom'] => f => {
  const {
    isQuestionEditor,
    question_id
  } = props;
  return <ForecastNoteCustom forecast={f} isQuestionEditor={isQuestionEditor ?? false} question_id={question_id} />;
};
function _temp(cfg) {
  return pick(cfg, ["publish"]);
}
function _temp2(f) {
  return isEmpty(f.cfg.status) && notNullOrEmpty(f.cfg.notes);
}
function _temp3(fg) {
  return fg.forecasts?.filter(_temp2) ?? [];
}
function _temp4(us) {
  return fromEntries(us.filter(notNull));
}